import React, { useState, useEffect, useRef } from "react";
import Confetti from "react-confetti";
import ShareButton from "./ShareButton";
import { ITEMS_PER_DAY, MAX_GUESSES } from "../utils/gameHelpers";
import { playPerfectDay, playGoodJob, playBoo, playFirstTry, playPhew } from "../utils/sounds";
import { supabase } from "../supabase";
import { calculateStreaks } from "../utils/streakCalc";

function BadgeTooltip({ children, text }) {
  const [show, setShow] = useState(false);
  return (
    <span
      className="badge-tooltip-wrapper"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && <span className="badge-tooltip">{text}</span>}
    </span>
  );
}

function SummaryImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  // If the image is already in the browser cache, mark loaded immediately
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      setLoaded(true);
    }
  }, []);

  return (
    <div className="summary-image-container">
      {!loaded && <div className="skeleton-image-sm" />}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        style={{ opacity: loaded ? 1 : 0, position: loaded ? "static" : "absolute" }}
      />
    </div>
  );
}

function ExpandableCard({ item, state, isHard, isFirst, isCloseCall, delay, expanded, onToggle }) {
  const hasDetails = (item.brand && item.brand.trim()) || (item.size && item.size.trim()) || (item.subcategory && item.subcategory.trim()) || (item.description && item.description.trim()) || (item.attributes && item.attributes.some(a => a && a.trim())) || item.is_weighted;

  return (
    <div
      className="card-expand-container"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div
        className={`summary-card ${state.status}${isHard && state.status === "won" ? " hard-win" : ""}${isFirst ? " first-try" : ""}${isCloseCall ? " close-call" : ""}`}
        onClick={() => hasDetails && onToggle()}
      >
        <SummaryImage src={item.image_url} alt={item.name} />
        <div className="summary-info">
          <p className="summary-name">{item.name}</p>
          <p className="summary-price">${(item.is_weighted && item.estimated_price != null ? item.estimated_price : item.price).toFixed(2)}</p>
          <p className="summary-result">
            {state.status === "won"
              ? `${isFirst ? "⚡ Ace" : isCloseCall ? "😅" : isHard ? "🔥" : "✓"} in ${state.guesses.length}`
              : "✗"}
          </p>
        </div>
        {hasDetails && <span className={`card-expand-hint${expanded ? " expanded" : ""}`}>▾</span>}
      </div>
      <div className={`card-details-dropdown${expanded ? " open" : ""}`}>
        <div className="card-details-tags">
          {item.brand && item.brand.trim() && (
            <BadgeTooltip text="Brand">
              <span className="card-detail-tag">{item.brand}</span>
            </BadgeTooltip>
          )}
          {item.size && item.size.trim() && (
            <BadgeTooltip text="Size">
              <span className="card-detail-tag">{item.size}</span>
            </BadgeTooltip>
          )}
          {item.subcategory && item.subcategory.trim() && (
            <BadgeTooltip text="Category">
              <span className="card-detail-tag">{item.subcategory}</span>
            </BadgeTooltip>
          )}
          {item.attributes && item.attributes.filter(a => a && a.trim()).map((a, i) => (
            <BadgeTooltip key={i} text="Attribute">
              <span className="card-detail-tag">{a}</span>
            </BadgeTooltip>
          ))}
        </div>
        {item.description && item.description.trim() && <p className="card-details-desc">{item.description}</p>}
        {item.is_weighted && (
          <div className="card-details-pricing">
            {item.unit_price && <p className="card-details-price-info">{item.unit_price}</p>}
            {item.estimated_price != null && <p className="card-details-price-info">Estimated total: ${item.estimated_price.toFixed(2)}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function GameSummary({ slotStates, dailyItems, todayStr, hardModeSlots, soundOn, user }) {
  const wins = Object.values(slotStates).filter((s) => s.status === "won").length;
  const totalGuesses = Object.values(slotStates).reduce((sum, s) => sum + s.guesses.length, 0);
  const hasFirstTry = Object.values(slotStates).some((s) => s.status === "won" && s.guesses.length === 1);
  const hasCloseCall = Object.values(slotStates).some((s) => s.status === "won" && s.guesses.length >= MAX_GUESSES);
  const aceCount = Object.values(slotStates).filter((s) => s.status === "won" && s.guesses.length === 1).length;
  const closeCallCount = Object.values(slotStates).filter((s) => s.status === "won" && s.guesses.length >= MAX_GUESSES).length;
  const isPerfect = wins === ITEMS_PER_DAY;
  const isAllLost = wins === 0;
  const isMostlyGood = wins >= 3 && !isPerfect;

  const [streak, setStreak] = useState(null);
  const [expandedSlots, setExpandedSlots] = useState(new Set());

  const toggleSlot = (slot) => {
    setExpandedSlots((prev) => {
      const next = new Set(prev);
      if (next.has(slot)) next.delete(slot);
      else next.add(slot);
      return next;
    });
  };

  // Fetch streak from Supabase if logged in
  useEffect(() => {
    if (!user) return;
    async function fetchStreak() {
      const { data } = await supabase
        .from("game_stats")
        .select("date")
        .eq("user_id", user.id);
      if (data) {
        const result = calculateStreaks(data);
        setStreak(result);
      }
    }
    fetchStreak();
  }, [user]);

  // Play sound based on outcome
  useEffect(() => {
    if (!soundOn) return;
    if (isPerfect) {
      playPerfectDay();
    } else if (hasFirstTry) {
      playFirstTry();
    } else if (hasCloseCall) {
      playPhew();
    } else if (isMostlyGood) {
      playGoodJob();
    } else if (isAllLost) {
      playBoo();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine celebration style
  let celebrationEmoji = "";
  let celebrationText = "";
  let confettiCount = 0;
  let confettiColors = undefined;

  if (isPerfect) {
    celebrationEmoji = "🎉";
    celebrationText = "Perfect Day!";
    confettiCount = 600;
  } else if (wins >= 4) {
    celebrationEmoji = "🔥";
    celebrationText = "So Close to Perfect!";
    confettiCount = 300;
    confettiColors = ["#ff9500", "#ffcc00", "#ff6b00"];
  } else if (wins >= 3) {
    celebrationEmoji = "💪";
    celebrationText = "Nice Job!";
    confettiCount = 150;
    confettiColors = ["#4eca6a", "#3da35d", "#ffc107"];
  } else if (wins >= 1) {
    celebrationEmoji = "👀";
    celebrationText = "Not Bad!";
    confettiCount = 0;
  } else {
    celebrationEmoji = "😬";
    celebrationText = "Erewhon Got You Today";
    confettiCount = 0;
  }

  return (
    <div className="game-container">
      <div className={`confetti-wrapper ${confettiCount > 0 ? "fade-in" : "fade-out"}`}>
        <Confetti recycle={false} numberOfPieces={confettiCount} colors={confettiColors} />
      </div>

      <div className={`summary-hero summary-hero-${wins}`}>
        <span className="summary-emoji">{celebrationEmoji}</span>
        <h2 className="summary-title">{celebrationText}</h2>
        <p className="summary-score">
          {wins}/{ITEMS_PER_DAY} correct in {totalGuesses} total guesses
        </p>
        {hasFirstTry && <BadgeTooltip text="Nailed the price on your very first guess!"><p className="first-try-badge">⚡ Ace!{aceCount > 1 && <span className="badge-count">{aceCount}</span>}</p></BadgeTooltip>}
        {hasCloseCall && <BadgeTooltip text="Got it right on your last chance - that was close!"><p className="close-call-badge">😅 Close call!{closeCallCount > 1 && <span className="badge-count">{closeCallCount}</span>}</p></BadgeTooltip>}
      </div>

      {streak && streak.currentStreak > 0 && (
        <div className={`streak-banner streak-tier-${streak.currentStreak >= 365 ? 365 : streak.currentStreak >= 180 ? 180 : streak.currentStreak >= 30 ? 30 : streak.currentStreak >= 7 ? 7 : streak.currentStreak >= 3 ? 3 : 1}`}>
          <span className="streak-fire">{streak.currentStreak >= 365 ? "👑" : streak.currentStreak >= 180 ? "💎" : streak.currentStreak >= 30 ? "⭐" : "🔥"}</span>
          <span className="streak-count">{streak.currentStreak}</span>
          <span className="streak-label">
            day streak
            {streak.currentStreak >= 365 ? " — Legendary!" :
             streak.currentStreak >= 180 ? " — Half-year hero!" :
             streak.currentStreak >= 30 ? " — Monthly master!" :
             streak.currentStreak >= 7 ? " — Weekly warrior!" :
             streak.currentStreak >= 3 ? " — On a roll!" : ""}
            {streak.currentStreak === streak.bestStreak && streak.currentStreak > 1 ? " 🏆 personal best!" : ""}
          </span>
        </div>
      )}

      <div className="summary-grid">
        {dailyItems.map((d, i) => {
          const item = d.items;
          const state = slotStates[d.slot] || {};
          const isHard = hardModeSlots?.[d.slot];
          const isFirst = state.status === "won" && state.guesses.length === 1;
          const isCloseCall = state.status === "won" && state.guesses.length >= MAX_GUESSES;
          return (
            <ExpandableCard
              key={d.slot}
              item={item}
              state={state}
              isHard={isHard}
              isFirst={isFirst}
              isCloseCall={isCloseCall}
              delay={i * 100}
              expanded={expandedSlots.has(d.slot)}
              onToggle={() => toggleSlot(d.slot)}
            />
          );
        })}
      </div>

      <ShareButton slotStates={slotStates} dailyItems={dailyItems} todayStr={todayStr} hardModeSlots={hardModeSlots} />

      <p className="play-again-msg">Come back tomorrow for 5 new items!</p>
      <p className="feedback-link">
        Have feedback?{" "}
        <a href="mailto:feedback@erewhondle.com?subject=Erewhondle Feedback">
          Let us know
        </a>
      </p>
    </div>
  );
}
